"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import DeckImage from "../DeckImage";
import type { DetailTab } from "../data/useDetailData";
import { ActionsSlot } from "./ActionsPlacement";
export type NavTab = {
  key: DetailTab;
  label: string;
  icon: ReactNode;
  count?: string;
};

/**
 * The chapter bar. ARIA tabs with a roving tabindex (arrows follow the
 * reading direction, Home/End jump); the chosen chapter sits on a pill that
 * slides between them. It pins under the header, and once it has pinned the
 * collection's logo and name slide into its start, so a reader deep in the
 * grid still knows whose works these are.
 */
export default function VitrineNav(t0) {
  const $ = _c(42);
  const {
    tabs,
    active,
    onChoose,
    ids,
    label,
    logo,
    name
  } = t0;
  const reduce = useReducedMotion();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const refs = useRef(t1);
  const sentinel = useRef(null);
  const [stuck, setStuck] = useState(false);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el = sentinel.current;
      if (!el) {
        return;
      }
      const observer = new IntersectionObserver(t4 => {
        const [entry] = t4;
        return setStuck(!entry.isIntersecting);
      }, {
        rootMargin: "-120px 0px 0px 0px"
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== active || $[4] !== reduce) {
    t4 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const tab = refs.current[active];
      const strip = tab?.parentElement;
      if (!tab || !strip || strip.scrollWidth <= strip.clientWidth) {
        return;
      }
      const t = tab.getBoundingClientRect();
      const b = strip.getBoundingClientRect();
      const delta = t.left < b.left + 14 ? t.left - b.left - 14 : t.right > b.right - 14 ? t.right - b.right + 14 : 0;
      if (delta) {
        strip.scrollBy({
          left: delta,
          behavior: reduce ? "auto" : "smooth"
        });
      }
    };
    $[3] = active;
    $[4] = reduce;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== active || $[7] !== reduce || $[8] !== stuck) {
    t5 = [active, reduce, stuck];
    $[6] = active;
    $[7] = reduce;
    $[8] = stuck;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== active || $[11] !== onChoose || $[12] !== tabs) {
    t6 = event => {
      const keys = tabs.map(_temp);
      const index = keys.indexOf(active);
      const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
      const step = {
        ArrowRight: rtl ? -1 : 1,
        ArrowLeft: rtl ? 1 : -1
      };
      let next = -1;
      if (event.key in step) {
        next = (index + step[event.key] + keys.length) % keys.length;
      } else {
        if (event.key === "Home") {
          next = 0;
        } else {
          if (event.key === "End") {
            next = keys.length - 1;
          }
        }
      }
      if (next < 0) {
        return;
      }
      event.preventDefault();
      onChoose(keys[next]);
      refs.current[keys[next]]?.focus();
    };
    $[10] = active;
    $[11] = onChoose;
    $[12] = tabs;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const move = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span ref={sentinel} className="vt-nav-sentinel" aria-hidden={true} />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = stuck || undefined;
  let t9;
  if ($[15] !== name) {
    t9 = name.charAt(0);
    $[15] = name;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <span>{t9}</span>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== logo || $[20] !== t10) {
    t11 = <span className="vt-nav-logo"><DeckImage src={logo} alt="" sizes="30px" fallback={t10} /></span>;
    $[19] = logo;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== name) {
    t12 = <span className="vt-nav-name">{name}</span>;
    $[22] = name;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <span className="vt-nav-id" aria-hidden={true}>{t11}{t12}</span>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== active || $[28] !== ids || $[29] !== onChoose || $[30] !== reduce || $[31] !== tabs) {
    t14 = tabs.map(tab_0 => {
      const selected = tab_0.key === active;
      return <button key={tab_0.key} ref={node => {
        refs.current[tab_0.key] = node;
      }} type="button" role="tab" id={ids.tab(tab_0.key)} aria-selected={selected} aria-controls={ids.panel} tabIndex={selected ? 0 : -1} className="vt-nav-tab v2-focus" onClick={() => onChoose(tab_0.key)}>{selected ? <motion.span className="vt-nav-pill" layoutId="vt-nav-pill" transition={reduce ? {
          duration: 0
        } : {
          type: "spring",
          stiffness: 480,
          damping: 38
        }} aria-hidden={true} /> : null}{tab_0.icon}<span>{tab_0.label}</span>{tab_0.count ? <b>{tab_0.count}</b> : null}</button>;
    });
    $[27] = active;
    $[28] = ids;
    $[29] = onChoose;
    $[30] = reduce;
    $[31] = tabs;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== label || $[34] !== move || $[35] !== t14) {
    t15 = <div className="vt-nav-tabs" role="tablist" aria-label={label} onKeyDown={move}>{t14}</div>;
    $[33] = label;
    $[34] = move;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ActionsSlot at="nav" />;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t13 || $[39] !== t15 || $[40] !== t8) {
    t17 = <>{t7}<div className="vt-nav" data-stuck={t8}>{t13}{t15}{t16}</div></>;
    $[38] = t13;
    $[39] = t15;
    $[40] = t8;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}
function _temp(x) {
  return x.key;
}